import React, { useState, useCallback, ElementType } from 'react';
import {
  Tab,
  TabList,
  TabPanels,
  Tabs,
  TabPanel,
  Text,
  Box,
  Icon,
  useColorModeValue,
  VStack,
  useToast,
} from '@chakra-ui/react';

import { useSettlementData } from 'src/hooks';
import { calculateDateRange, timeRanges } from 'src/utils';
import SettledOrders from './shared/SettledOrders';
import UnsettledOrders from './shared/UnsettledOrders';
import { FiFileText } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { LoadingSpinner, ModernCard, PageHeader } from './shared';
import { useUserDefaultRole, useUserId } from '@nhost/react';
import { useGetRestaurantByUserIdQuery } from 'src/utils/__generated__/graphql';
const MotionBox = motion(Box);

export const Transcation: React.FC = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('transactionActiveTab');
    return savedTab ? Number(savedTab) : 0;
  });
  const [timeRange, setTimeRange] = useState<string>(() => {
    return localStorage.getItem('timeRange') || 'today';
  });
  const toast = useToast();
  const accentColor = useColorModeValue('brand.500', 'brand.300');

  const userRole = useUserDefaultRole();

  const userId = useUserId();
  const {
    data,
  } = useGetRestaurantByUserIdQuery({
    variables: { userId },
    skip: userRole !== 'seller',
  });
  const restaurantId = data?.restaurant?.[0]?.id;

  const { startDate, endDate } = calculateDateRange(timeRange);
  const { unsettledData, settledData, loading, unsettledError, settledError } = useSettlementData(
    startDate,
    endDate,
    restaurantId
  );

  const handleTimeRangeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newTimeRange = e.target.value;
      setTimeRange(newTimeRange);
      localStorage.setItem('timeRange', newTimeRange);
    },
    [setTimeRange]
  );

  const handleActiveTab = useCallback(
    (e: number) => {
      setActiveTab(e);
      localStorage.setItem('transactionActiveTab', e.toString());
    },
    [setActiveTab]
  );

  if (loading) return <LoadingSpinner text="Loading transaction data..." />;

  if (unsettledError || settledError) {
    toast({
      title: 'Error',
      description: unsettledError
        ? `Error fetching unsettled data: ${unsettledError?.message}`
        : `Error fetching settled data: ${settledError?.message}`,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return (
      <ModernCard p={8}>
        <VStack spacing={4} textAlign="center">
          <Text fontSize="lg" fontWeight="semibold" color="red.500">
            Error Loading Transaction Data
          </Text>
          <Text color="gray.600">
            {unsettledError
              ? `Error fetching unsettled data: ${unsettledError?.message}`
              : `Error fetching settled data: ${settledError?.message}`}
          </Text>
        </VStack>
      </ModernCard>
    );
  }

  return (
    <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <PageHeader
        title="Orders Transaction Report"
        subtitle="View and manage all transaction records"
        icon={<Icon as={FiFileText as ElementType} color={accentColor} boxSize={6} />}
      />

      <Tabs
        size={{ base: 'sm', md: 'md' }}
        variant="soft-rounded"
        colorScheme="brand"
        index={activeTab}
        onChange={handleActiveTab}
        isLazy
        p={{ base: 4, md: 6 }}
      >
        <Box
          borderRadius="xl"
          overflow="hidden"
          backgroundColor={useColorModeValue('white', 'gray.800')}
          shadow="sm"
          pt={6}
          px={4}
          alignItems="center"
          display="flex"
        >
          <TabList mb={{ base: 4, md: 6 }}>
            <Tab
              _selected={{ color: 'white', bg: 'brand.500' }}
              fontWeight="medium"
              fontSize={{ base: 'sm', md: 'md' }}
            >
              Unsettled Orders
            </Tab>
            <Tab
              _selected={{ color: 'white', bg: 'brand.500' }}
              fontWeight="medium"
              fontSize={{ base: 'sm', md: 'md' }}
            >
              Settled Orders
            </Tab>
          </TabList>
        </Box>

        <TabPanels>
          <TabPanel px={0}>
            <UnsettledOrders data={unsettledData} />
          </TabPanel>
          <TabPanel px={0}>
            <SettledOrders
              data={settledData}
              timeRange={timeRange}
              handleTimeRangeChange={handleTimeRangeChange}
              timeRanges={timeRanges}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </MotionBox>
  );
};
